/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useLogger } from '@airasia-rn/core-logger';
import { useFeedback } from '../../context/FeedbackContext';
import { useScreenshotTakenListener } from '../../hooks/useScreenshotTakenListener';
import {
  isScreenshotDetectorAvailable,
  type ScreenshotTakenPayload,
} from '../../native/NativeScreenshotDetector';
import type { ScreenDetails } from '../../types';

export interface ScreenshotFeedbackListenerProps {
  /** Receives LOB-registered details, if any; return null to skip the overlay. */
  getScreenDetails: (registered: ScreenDetails | null) => ScreenDetails | null;
}

/**
 * Listens for screenshot events via native `ScreenshotDetector`
 * and shows the feedback overlay. Mount inside FeedbackProvider; host provides getScreenDetails.
 */
export function ScreenshotFeedbackListener({
  getScreenDetails,
}: ScreenshotFeedbackListenerProps) {
  const { showScreenshotOverlay, screenDetailsRef } = useFeedback();
  const { logDebug } = useLogger();
  const getScreenDetailsRef = useRef(getScreenDetails);
  getScreenDetailsRef.current = getScreenDetails;

  useEffect(() => {
    logDebug?.('[NPS Screenshot] ScreenshotFeedbackListener mounted', {
      platform: Platform.OS,
      isScreenshotDetectorAvailable,
    });
  }, [logDebug]);

  const onScreenshot = async (payload?: ScreenshotTakenPayload) => {
    logDebug?.(
      '[NPS Screenshot] screenshot detected, calling showScreenshotOverlay',
      { payload },
    );
    const details = getScreenDetailsRef.current(
      screenDetailsRef?.current ?? null,
    );
    if (details == null) return;
    // Use native image when available: iOS = app-captured key window (no system thumbnail); Android = host app window capture (file URI).
    const uri: string | null = payload?.screenshotUri ?? null;
    showScreenshotOverlay(details, uri);
    logDebug?.('[NPS Screenshot] showScreenshotOverlay called', {
      screenName: details.screenName,
      hasUri: Boolean(uri),
    });
  };

  useScreenshotTakenListener(onScreenshot);

  return null;
}
